'use client';

import { useRef } from 'react';

import { useQuery } from '@lichtblick/apollo';
import { useTranslation } from '@lichtblick/i18next';
import { Edit, Refresh } from '@lichtblick/icons';
import { TextVariants } from '@lichtblick/theme';
import {
  GET_SINGLE_ORDER,
  GET_WORK_ORDER_APPOINTMENT_PM,
  GetSingleWorkOrderQuery,
  GetWorkOrderAppointmentsPmQuery,
  GetWorkOrderAppointmentsPmQueryVariables,
  ListSortDirection,
  QueryGetWorkOrder_V1Args,
} from '@lichtblick/types';
import { createWorkOrderCalendarUrl, useLogoutOnApolloError } from '@lichtblick/utils';

import { EmptyTableWarning } from './EmptyTableWarning';
import { AppointmentsContainer, BtnContainer, Container, TextWrapper } from './WorkorderAppointments.styles';

import { Button, IconButton, SkeletonComponent, Text, WorkOrderAppointmentsTable } from '..';

export const WorkorderAppointments = ({ orderId }: { orderId: number }) => {
  const { t } = useTranslation();
  const linkRef = useRef<HTMLAnchorElement>(null);

  const { data, error: workOrderError } = useQuery<GetSingleWorkOrderQuery, QueryGetWorkOrder_V1Args>(
    GET_SINGLE_ORDER,
    {
      variables: { input: { workOrderId: orderId } },
    },
  );

  const {
    data: appointments,
    error: appointmentsError,
    loading: isLoading,
    refetch,
  } = useQuery<GetWorkOrderAppointmentsPmQuery, GetWorkOrderAppointmentsPmQueryVariables>(
    GET_WORK_ORDER_APPOINTMENT_PM,
    {
      variables: {
        input: {
          workOrderId: orderId,
          orderBy: [{ direction: ListSortDirection.Ascending, property: 'startAtUtc' }],
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  useLogoutOnApolloError(workOrderError);
  useLogoutOnApolloError(appointmentsError);

  if (isLoading) {
    return <SkeletonComponent height="40rem" width="100%" />;
  }

  const url = createWorkOrderCalendarUrl(orderId);

  return (
    <Container>
      <TextWrapper>
        <Text $variant={TextVariants.title.m}>{t('pm:orderMonday')}</Text>
        <Text $variant={TextVariants.header.m}>{data?.getWorkOrder_V1?.data?.name}</Text>
      </TextWrapper>
      <BtnContainer>
        <a href={url} ref={linkRef} rel="noopener noreferrer" style={{ display: 'none' }} target="_blank">
          Open Link
        </a>
        <Button
          icon={<Edit />}
          label={t('pm:addAppointment')}
          onClick={() => {
            if (linkRef.current) {
              linkRef.current.click();
            }
          }}
        />
        <IconButton icon={<Refresh />} onClick={() => refetch()} tooltip={t('pm:refresh')} />
      </BtnContainer>
      <AppointmentsContainer>
        {appointments?.getWorkOrderAppointments_V1?.data?.length ? (
          <WorkOrderAppointmentsTable appointments={appointments?.getWorkOrderAppointments_V1.data} />
        ) : (
          <EmptyTableWarning reset={() => refetch()} />
        )}
      </AppointmentsContainer>
    </Container>
  );
};
